import { useEffect } from "react";
import { Helmet } from "react-helmet";

const DEFAULT_SEO = {
  siteName: "Lifely",
  title: "Lifely - Map What Matters",
  description:
    "Lifely helps you organize your digital estate, crypto holdings, and life's important details — so nothing gets lost. Structured clarity for what matters most.",
  keywords:
    "digital estate planning, crypto inheritance, continuity planning, digital legacy planning, crypto estate planning, self-custody inheritance, digital asset organization, estate planning tool",
  url: "https://lifely.guide",
  image: "https://lifely.guide/og-image.png",
  twitterHandle: "@lifelyguide",
  type: "website",
};

export function SEO({
  title,
  description,
  keywords,
  path = "",
  image,
  type = "website",
  noindex = false,
}) {
  const pageTitle = title
    ? `${title} | ${DEFAULT_SEO.siteName}`
    : DEFAULT_SEO.title;
  const pageDescription = description || DEFAULT_SEO.description;
  const pageKeywords = keywords || DEFAULT_SEO.keywords;
  const pageUrl = `${DEFAULT_SEO.url}${path}`;
  const pageImage = image || DEFAULT_SEO.image;

  // Update document title via useEffect to avoid react-helmet title issues
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <Helmet>
      {/* Primary Meta Tags - title is set via useEffect */}
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
      <meta property="og:locale" content="en_NZ" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />

      {/* Additional SEO */}
      <meta name="author" content="Lifely Holdings Limited" />
      <meta name="publisher" content="Lifely Holdings Limited" />
      <meta name="geo.region" content="NZ" />
      <meta name="geo.placename" content="New Zealand" />
    </Helmet>
  );
}

// Pre-configured SEO for each page
export const PAGE_SEO = {
  home: {
    title: null, // Uses default "Lifely - Map What Matters"
    description:
      "Lifely helps you organize your digital estate, crypto holdings, and life's important details — so nothing gets lost. Structured clarity for what matters most.",
    path: "/",
  },
  howItWorks: {
    title: "How It Works",
    description:
      "Learn how Lifely helps you prepare for a will, maintain a living companion to your estate plan, and gain portfolio clarity across crypto and traditional assets.",
    keywords:
      "how lifely works, estate planning process, crypto organization, digital will preparation, continuity planning steps",
    path: "/how-it-works",
  },
  about: {
    title: "About",
    description:
      "Lifely provides structured clarity for your digital life. Organize crypto, investments, property, and important details without storing sensitive secrets.",
    keywords:
      "about lifely, digital estate platform, crypto organization tool, estate planning software, continuity planning service",
    path: "/about",
  },
  ourStory: {
    title: "Our Story",
    description:
      "Lifely was born from personal experience — navigating an estate without visibility. We built it so others don't have to learn the same lessons the hard way.",
    keywords:
      "lifely story, why lifely, estate planning personal experience, crypto inheritance story, founder story",
    path: "/our-story",
  },
  insideLifely: {
    title: "Inside Lifely",
    description:
      "Take a tour inside Lifely. See how the dashboard, maps, continuity features, and sharing controls help you organize what matters most.",
    keywords:
      "lifely features, lifely dashboard, lifely app tour, estate planning features, continuity check-in",
    path: "/inside-lifely",
  },
  pricing: {
    title: "Pricing",
    description:
      "Choose the Lifely plan that fits your needs. Start free with Insights, or map your estate with Standard. Plus plan coming soon.",
    keywords:
      "lifely pricing, estate planning cost, digital asset organization pricing, crypto continuity pricing",
    path: "/pricing",
  },
  contact: {
    title: "Contact",
    description:
      "Get in touch with the Lifely team. Questions about digital estate planning, crypto inheritance, or continuity planning? We'd love to hear from you.",
    keywords:
      "contact lifely, lifely support, estate planning help, digital asset questions",
    path: "/contact",
  },
  privacy: {
    title: "Privacy Policy",
    description:
      "Lifely Privacy Policy. Learn how we collect, use, and protect your personal information. Data minimization and security are core to our approach.",
    path: "/privacy",
    noindex: false,
  },
  terms: {
    title: "Terms of Service",
    description:
      "Lifely Terms of Service. Read our terms and conditions for using the Lifely digital estate planning platform.",
    path: "/terms-of-service",
    noindex: false,
  },
  upgrade: {
    title: "Upgrade",
    description:
      "Upgrade your Lifely plan. Go beyond insights and start mapping your important information with Lifely Standard or Plus.",
    path: "/upgrade",
    noindex: true,
  },
};
